"use client";

/**
 * Informational banner shown to non-payer adults whose current household is
 * covered by another adult's subscription (Decision #7 in
 * [[shared-subscription-any-household-adult-can-pay-co]]).
 *
 * Communicates who is paying, until when, and offers a "Manage" entry point
 * — clicking through opens the payer's Customer Portal (with a privacy-note
 * confirmation since the portal exposes the payer's billing details).
 */

import { useEffect, useState } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { useAuthContext } from "@/contexts/AuthContext";
import { useHouseholdContext } from "@/contexts/HouseholdContext";
import { useHouseholdSubscription } from "@/hooks/useHouseholdSubscription";
import { redirectToBillingPortal } from "@/lib/billing";
import type { Timestamp } from "firebase/firestore";

function formatDate(ts: Timestamp | null | undefined): string {
  if (!ts) return "—";
  try {
    return ts.toDate().toLocaleDateString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return "—";
  }
}

interface CoveredByBannerProps {
  /** Optional override of the active household (else uses HouseholdContext). */
  householdId?: string;
}

export function CoveredByBanner({ householdId: householdIdOverride }: CoveredByBannerProps = {}) {
  const { user } = useAuthContext();
  const { householdId: contextHouseholdId } = useHouseholdContext();
  const { state, loading } = useHouseholdSubscription();
  const [payerName, setPayerName] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [opening, setOpening] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const householdId = householdIdOverride ?? contextHouseholdId;
  const sub = state.subscription;
  const payerUid = sub?.payer_uid ?? null;
  const isViewerThePayer = payerUid != null && user?.uid === payerUid;

  // Only render for non-payer adults in a household with an active payer.
  const shouldRender =
    !loading
    && Boolean(householdId)
    && Boolean(payerUid)
    && !isViewerThePayer
    && state.isActive;

  useEffect(() => {
    if (!shouldRender || !payerUid) return;
    const ref = doc(db, "member_profiles", payerUid);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        const data = snap.data() as { nickname?: string; name?: string } | undefined;
        setPayerName(data?.nickname || data?.name || "another adult");
      },
      () => setPayerName("another adult"),
    );
    return unsub;
  }, [shouldRender, payerUid]);

  if (!shouldRender) return null;

  const expires = formatDate(sub?.current_period_end ?? sub?.expires_at);
  const tierLabel = tierShortLabel(sub?.tier);

  async function handleManage() {
    setError(null);
    setOpening(true);
    try {
      await redirectToBillingPortal({ householdId: householdId ?? undefined });
    } catch (err) {
      console.error("redirectToBillingPortal failed", err);
      setError("Couldn't open the billing portal. Please try again.");
      setOpening(false);
    }
  }

  return (
    <>
      <div
        role="status"
        className="bg-emerald-50 dark:bg-emerald-900/30 text-emerald-900 dark:text-emerald-100 px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 border-b border-emerald-100 dark:border-emerald-900/50"
      >
        <div className="flex-1">
          <p className="text-sm font-semibold">
            {`Covered by ${payerName ?? "another adult"} · ${tierLabel}`}
          </p>
          <p className="text-xs opacity-80 mt-0.5">
            {`Renews ${expires}`}
          </p>
        </div>
        <button
          onClick={() => setConfirming(true)}
          className="shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold bg-white dark:bg-emerald-100 text-emerald-800 hover:bg-emerald-50 dark:hover:bg-white transition-colors"
        >
          View billing
        </button>
      </div>

      {confirming && (
        <PrivacyConfirmModal
          payerName={payerName ?? "another adult"}
          onCancel={() => setConfirming(false)}
          onConfirm={handleManage}
          opening={opening}
          error={error}
        />
      )}
    </>
  );
}

function tierShortLabel(tier: string | undefined | null): string {
  switch (tier) {
    case "zoey_plus":
      return "Zoey+";
    case "zoey_family":
      return "Zoey Family";
    case "zoey_starter":
      return "Zoey Starter";
    case "founders_beta":
      return "Founders Beta";
    case "trial":
      return "Trial";
    default:
      return "Active";
  }
}

interface PrivacyConfirmModalProps {
  payerName: string;
  onCancel: () => void;
  onConfirm: () => void;
  opening: boolean;
  error: string | null;
}

function PrivacyConfirmModal({
  payerName,
  onCancel,
  onConfirm,
  opening,
  error,
}: PrivacyConfirmModalProps) {
  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center">
      <div
        className="absolute inset-0 bg-black/60"
        onClick={opening ? undefined : onCancel}
        role="presentation"
      />
      <div
        className="relative bg-white dark:bg-gray-900 w-full sm:max-w-md sm:mx-4 sm:rounded-2xl rounded-t-2xl shadow-2xl max-h-[92vh] overflow-y-auto"
        role="dialog"
        aria-modal="true"
        aria-labelledby="portal-privacy-title"
      >
        <div className="px-5 pt-5 pb-3">
          <h2
            id="portal-privacy-title"
            className="text-lg font-bold text-gray-900 dark:text-gray-100"
          >
            {`Viewing ${payerName}'s billing`}
          </h2>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            {
              "The Stripe Customer Portal will show this household's payment method, billing address, invoice history, and email — they belong to "
            }
            <span className="font-semibold">{payerName}</span>
            {", not you. Continue?"}
          </p>
          {error && (
            <p
              role="alert"
              className="mt-3 rounded-xl border border-red-200 dark:border-red-900/40 bg-red-50 dark:bg-red-900/20 px-3 py-2 text-xs text-red-700 dark:text-red-300"
            >
              {error}
            </p>
          )}
        </div>
        <div className="px-5 py-4 border-t border-gray-100 dark:border-white/5 flex flex-col sm:flex-row gap-2 sm:justify-end">
          <button
            onClick={onCancel}
            disabled={opening}
            className="order-2 sm:order-1 px-4 py-2.5 rounded-xl text-sm font-semibold text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-white/10 hover:bg-gray-200 dark:hover:bg-white/15 transition-colors disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={opening}
            className="order-1 sm:order-2 px-5 py-2.5 rounded-xl text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700 transition-colors disabled:opacity-60"
          >
            {opening ? "Opening…" : "Continue"}
          </button>
        </div>
      </div>
    </div>
  );
}
